/**
 * Flowbite React global theme — maps the app's warm-brown brand palette
 * onto Flowbite's "default" / component color slots.
 *
 * Used in <ThemeProvider theme={flowbiteTheme}> at the app root.
 */
import { createTheme } from 'flowbite-react';

const BRAND      = '#8b5a3c';
const BRAND_DARK = '#6d4226';

// Tailwind arbitrary-value classes for safe purge (these will be in the bundle
// because they appear as string literals here, which Tailwind v4 scans).
export const BRAND_BTN =
  `bg-[${BRAND}] hover:bg-[${BRAND_DARK}] focus:ring-[${BRAND}]/30 text-white border-transparent`;

export const BRAND_OUTLINE =
  `border border-[${BRAND}] text-[${BRAND}] hover:bg-[${BRAND}] hover:text-white focus:ring-[${BRAND}]/30 bg-transparent`;

export const flowbiteTheme = createTheme({
  button: {
    color: {
      // "brand" — primary actions (warm brown)
      brand:
        `${BRAND_BTN}`,
      // keep all standard colors untouched; override "default" to match brand
      default:
        `${BRAND_BTN}`,
    },
    outlineColor: {
      brand: `${BRAND_OUTLINE}`,
    },
  },

  badge: {
    // Soft-style badges — add colours that StatusBadge uses
    root: {
      color: {
        success:  'bg-emerald-100 text-emerald-800',
        warning:  'bg-amber-100 text-amber-800',
        failure:  'bg-red-100 text-red-800',
        info:     'bg-blue-100 text-blue-800',
        gray:     'bg-gray-100 text-gray-700',
        purple:   'bg-purple-100 text-purple-800',
        indigo:   'bg-indigo-100 text-indigo-800',
      },
    },
  },

  modal: {
    root: {
      base: 'fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden',
      show: {
        on:  'flex',
        off: 'hidden',
      },
    },
    content: {
      base: 'relative w-full max-h-full',
      inner:
        'relative flex max-h-[90dvh] flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-800',
    },
    header: {
      base: 'flex items-center justify-between rounded-t-2xl border-b border-[#e8dcc6] p-4',
      title: 'text-lg font-semibold text-[#2d1f14]',
      popup: 'border-b-0 p-2',
      close: {
        base: 'ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900',
        icon: 'h-5 w-5',
      },
    },
    body: {
      base: 'flex-1 overflow-auto p-4',
      popup: 'pt-0',
    },
    footer: {
      base: 'flex items-center space-x-2 rounded-b-2xl border-t border-[#e8dcc6] p-4',
      popup: 'border-t-0',
    },
  },

  table: {
    root: {
      base: 'w-full text-left text-sm text-gray-500',
      shadow: 'absolute left-0 top-0 -z-10 h-full w-full rounded-lg bg-white drop-shadow-md',
      wrapper: 'relative',
    },
    body: {
      base: 'group/body',
      cell: {
        base: 'px-4 py-3 group-first/body:group-first/row:first:rounded-tl-lg group-first/body:group-first/row:last:rounded-tr-lg group-last/body:group-last/row:first:rounded-bl-lg group-last/body:group-last/row:last:rounded-br-lg',
      },
    },
    head: {
      base: 'group/head text-xs uppercase text-gray-700',
      cell: {
        base: 'bg-[#f4e8d8] px-4 py-3 group-first/head:first:rounded-tl-lg group-first/head:last:rounded-tr-lg',
      },
    },
    row: {
      base: 'group/row',
      hovered: 'hover:bg-[#faf6f0]',
      striped: 'odd:bg-white even:bg-[#faf8f5]',
    },
  },
});
